/**
 * The sound an agent hears when a call arrives.
 *
 * WHY THIS EXISTS. Until now the console made no sound at all: a live customer
 * was dropped into an agent's headset with nothing to announce them, and the
 * only cue was a visual change on a tab they might not be looking at. On
 * 2026-09-21/22 that produced 503 RINGNOANSWER events — every one at exactly
 * 20,000ms, the full member timeout, meaning the agent never clicked at all.
 * The miss rate ran from 3% (William, Nicol) to 34% (Nelson), which is a
 * spread of attention, not of equipment. 59 callers who abandoned had a missed
 * ring earlier on the same call.
 *
 * WHY THE BROWSER AND NOT ASTERISK. Asterisk can only play audio to an agent
 * AFTER they answer — `Queue()`'s announce runs post-pickup. The sound has to
 * arrive while the phone is still ringing, and the browser is the only place
 * that can happen.
 *
 * WHY THE CUSTOMER CANNOT HEAR IT. During ringing there is no RTCPeerConnection
 * at all — SIP.js sets up media only when the session reaches Established. The
 * browser is sending nothing but SIP signalling, so there is no path to the
 * customer for this audio to take. That is structural, not a precaution.
 *
 * ON "EXTREMELY LOUD". The operator asked for an extremely loud beep. What is
 * built here is deliberately not that. The tone and the call share one output
 * device and one volume setting, so a full-scale burst through a headset an
 * agent has turned up to hear a quiet customer is acoustic-shock territory —
 * a recognised call-centre injury. What makes an alert unmissable is contrast,
 * onset and repetition, not energy: this sits about 10-15 dB above speech in
 * the 1-2 kHz band where the ear is most sensitive, and repeats every 1.4
 * seconds until answered. It will be impossible to miss and it will not hurt
 * anyone.
 */

/** Ring pattern, in seconds unless noted. */
const PULSE_S = 0.12;
const GAP_S = 0.08;
const CYCLE_S = 1.4;
/** Hard stop. The member timeout is 20s; this outlives it and no longer. */
const MAX_RING_S = 25;
/** The first cycles are softer — an attentive agent never hears the loud one. */
const SOFT_CYCLES = 2;
const SOFT_FACTOR = 0.6;

const KEY_VOLUME = "agent.ringVolume";

export class AlertTone {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private live: OscillatorNode[] = [];

  /** 0.4 - 1.0. Per agent, because headsets and ears differ. */
  get volume(): number {
    try {
      const v = parseFloat(window.localStorage.getItem(KEY_VOLUME) ?? "");
      if (Number.isFinite(v)) return Math.min(1, Math.max(0.4, v));
    } catch {
      /* private window, blocked storage — fall through to the default */
    }
    return 0.8;
  }

  set volume(v: number) {
    const clamped = Math.min(1, Math.max(0.4, v));
    try {
      window.localStorage.setItem(KEY_VOLUME, String(clamped));
    } catch {
      /* not worth failing a call over */
    }
    if (this.master && this.ctx) {
      this.master.gain.setValueAtTime(clamped, this.ctx.currentTime);
    }
  }

  /** Must be called from a user gesture, or the context starts suspended and
   *  every later sound is silently dropped by the autoplay policy. */
  unlock(): void {
    try {
      if (!this.ctx) {
        const Ctor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.volume;
        this.master.connect(this.ctx.destination);
      }
      void this.ctx.resume();
    } catch {
      /* no WebAudio (jsdom, locked-down browser) — the console still works */
    }
  }

  /** iOS suspends the context when the tab is backgrounded. */
  resumeIfSuspended(): void {
    try {
      if (this.ctx?.state === "suspended") void this.ctx.resume();
    } catch {
      /* best effort */
    }
  }

  /** "running" | "suspended" | "unavailable" — so the UI can say why it is
   *  silent instead of the agent discovering it on a missed call. */
  state(): string {
    return this.ctx ? this.ctx.state : "unavailable";
  }

  /**
   * Ring until told otherwise.
   *
   * The ENTIRE pattern is scheduled up front against the AudioContext clock.
   * A setTimeout/setInterval chain would be throttled to a crawl in a
   * background tab — which is precisely the tab an agent has stopped looking
   * at, i.e. exactly when the sound matters most.
   */
  startRing(): void {
    this.stopRing();
    this.unlock();
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;

    const t0 = ctx.currentTime;

    for (let cycle = 0; cycle * CYCLE_S < MAX_RING_S; cycle++) {
      const base = t0 + cycle * CYCLE_S;
      const scale = cycle < SOFT_CYCLES ? SOFT_FACTOR : 1;
      this.pulse(base, scale);
      this.pulse(base + PULSE_S + GAP_S, scale);
    }
  }

  stopRing(): void {
    for (const o of this.live) {
      try {
        o.stop();
        o.disconnect();
      } catch {
        /* already stopped */
      }
    }
    this.live = [];
  }

  /** The moment of connection — one short chirp so the agent knows the
   *  customer is now live and does not talk over their first word. */
  playConnect(): void {
    this.stopRing();
    this.unlock();
    if (!this.ctx) return;
    this.tone(this.ctx.currentTime, 0.15, 1000, 0.25, 0.005, 0.04);
  }

  /** Played when the agent goes Online: doubles as the gesture that unlocks
   *  audio and as a "can you actually hear this?" check. */
  playConfirm(): void {
    this.unlock();
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.tone(t, 0.08, 880, 0.18, 0.005, 0.03);
    this.tone(t + 0.12, 0.08, 1320, 0.18, 0.005, 0.03);
  }

  close(): void {
    this.stopRing();
    try {
      void this.ctx?.close();
    } catch {
      /* nothing to do */
    }
    this.ctx = null;
    this.master = null;
  }

  /** One ring pulse: two sines, the upper quieter, summed well below full
   *  scale. Sines rather than square/saw — a harsh waveform is what makes an
   *  alert painful rather than merely audible. */
  private pulse(at: number, scale: number): void {
    this.tone(at, PULSE_S, 1000, 0.32 * scale, 0.008, 0.03);
    this.tone(at, PULSE_S, 1500, 0.16 * scale, 0.008, 0.03);
  }

  private tone(
    at: number, dur: number, freq: number,
    peak: number, attack: number, release: number,
  ): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    try {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, at);
      // Ramped, never stepped: an instantaneous edge is a click, and clicks
      // are what people describe as "painful".
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(peak, at + attack);
      g.gain.setValueAtTime(peak, at + dur - release);
      g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
      osc.connect(g);
      g.connect(master);
      osc.start(at);
      osc.stop(at + dur + 0.01);
      this.live.push(osc);
      osc.onended = () => {
        this.live = this.live.filter((o) => o !== osc);
      };
    } catch {
      /* one failed tone must never break call handling */
    }
  }
}
